import { createTheme } from '@mui/material/styles'
import { getLocalStorage } from './utils/helper'
import { buildPrimaryPalette } from './utils/theming/buildPrimaryPalette'
import { applyPrimaryCssVariables } from './utils/theming/applyPrimaryCssVariables'
import { seedFromPayload, toHex } from './utils/theming/buildMobileScheme'

// Fallback brand color when no tenant theme is configured yet (matches the previous default).
export const DEFAULT_BRAND_COLOR = '#456DB3'

const getStoredTheme = () => {
  const rawTheme = getLocalStorage('theme')
  if (!rawTheme) return null

  try {
    return JSON.parse(rawTheme)
  } catch {
    return null
  }
}

/**
 * Screens reached before a subsidiary context exists render in default nAI branding, not the
 * previously-selected tenant's, which would otherwise leak in from localStorage after a logout.
 *
 * Matched per path segment, not by substring — a route like `/select-workspace-summary`
 * must not match (see theme.test.ts).
 */
const UNTHEMED_ROUTE_SEGMENTS = new Set(['login', 'otp-verification', 'select-workspace'])

export const isUnthemedPath = (pathname: string): boolean => pathname.split('/').some((segment) => UNTHEMED_ROUTE_SEGMENTS.has(segment.toLowerCase()))

// Decided once at module load, which is sound because every transition into or out of the
// themed area is a full page load: OTP -> select-workspace, select-workspace -> dashboard and
// logout all navigate via window.location, and login <-> otp-verification stays within the
// unthemed set. ponytail: boot-time check — make it reactive if any of those become SPA
// navigations, or the tenant theme will persist onto an unthemed screen.
const localStorageTheme = isUnthemedPath(window.location.pathname) ? null : getStoredTheme()

// The phone seeds from `color_1`, so this does too; `primary` is the fallback because
// ThemeAdd writes both from the same colour picker and they cannot disagree unless a row
// was edited outside that form. Parsing first because the payload mixes formats in one
// object: `primary` arrives as hex and `color_1` as `rgba(69, 109, 179, 1)`.
const tenantColor = seedFromPayload(localStorageTheme?.colors)

// Everything below is generated from this one color by the same Material 3 algorithm the
// Flutter app runs, so a tenant sees the same brand in both clients.
const primaryPalette = buildPrimaryPalette(tenantColor || DEFAULT_BRAND_COLOR)
const scheme = primaryPalette.scheme

// The status colours the phone reads straight from the payload rather than generating.
// Falling back to the previous hardcoded values keeps a partial payload renderable.
const backendColors = (localStorageTheme?.colors ?? {}) as Record<string, string | undefined>
const successColor = toHex(backendColors.color_7) || '#059669'
const warningColor = toHex(backendColors.color_6) || '#d97706'
const infoColor = toHex(backendColors.color_10) || '#6270f0'

// Mirrors the same derived palette into CSS variables so Tailwind's `primary-*` classes
// (tailwind.config.js) render the identical colors as MUI, from this one computation.
applyPrimaryCssVariables(primaryPalette)

const theme = createTheme({
  typography: {
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.125rem', fontWeight: 650, lineHeight: 1.4 },
    h4: { fontSize: '1rem', fontWeight: 650, lineHeight: 1.45 },
    body1: { fontSize: '0.875rem', lineHeight: 1.55 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
  },
  palette: {
    // `tones` and `scheme` below aren't part of MUI's PaletteColorOptions type; cast avoids
    // fighting the type-checker over the custom fields, matching this codebase's convention.
    primary: {
      main: primaryPalette.main,
      light: primaryPalette.light,
      dark: primaryPalette.dark,
      contrastText: primaryPalette.contrastText,
      // Full generated tonal ramp for anything beyond main/light/dark, e.g.
      // `theme.palette.primary.tones[50]` for a pale surface fill.
      tones: primaryPalette.tones,
      // Every Material role, for surfaces and containers that have no MUI slot, e.g.
      // `theme.palette.primary.scheme.secondaryContainer`.
      scheme,
    },
    secondary: {
      // Generated, not read from theme_json.colors.secondary — the phone ignores that field
      // too. Present so `color="secondary"` renders in-brand instead of MUI's default purple.
      main: scheme.secondary,
      light: scheme.secondaryContainer,
      dark: scheme.onSecondaryContainer,
      contrastText: scheme.onSecondary,
    },
    error: {
      main: scheme.error,
      light: scheme.errorContainer,
      contrastText: scheme.onError,
    },
    success: {
      main: successColor,
    },
    warning: {
      main: warningColor,
    },
    info: {
      main: infoColor,
    },
    // Neutral, not the M3 surface roles. material-react-table paints its cells from
    // background.default, so a hue-tinted surface (#fff8f6 for a maroon tenant) washes every
    // grid in the app. These are the only two neutrals MUI hands to components implicitly,
    // and nothing else in src reads them — so keeping them neutral is effectively scoped to
    // tables, and matches the values dev ships.
    // Neutral, not the M3 surface roles: material-react-table paints its cells from
    // background.default, so a hue-tinted surface (#fff8f6 for a maroon tenant) washes every
    // grid in the app. These are the values dev ships, and the same ones index.css declares
    // as --app-bg / --app-surface.
    //
    // Literal hex, NOT var(--app-bg): MUI parses palette colours through decomposeColor, and
    // MRT calls lighten(palette.background.default) at mount — a CSS variable throws
    // "Unsupported `var(--app-bg)` color" and takes the whole table down. Only `sx` and
    // component styleOverrides may reference CSS variables.
    background: {
      default: '#f8f9fb',
      paper: '#ffffff',
    },
    text: {
      primary: scheme.onSurface,
      secondary: scheme.onSurfaceVariant,
      disabled: scheme.outline,
    },
    // Neutral (dev's value): MRT renders a MUI <Divider> as the vertical rule between header
    // cells, and outlineVariant is hue-tinted (#ddc0bb for a maroon tenant), which shows up as
    // salmon bars across every table header. Literal hex — palette values go through
    // decomposeColor and cannot be var(), see theme.palette.test.ts.
    divider: '#e2e6ee',
    common: {
      black: '#000',
    },
  } as any,
  shape: {
    borderRadius: 10,
  },
  // Geometry unchanged; only the shadow colour moves, from the old blue-black
  // rgba(17, 24, 39, x) to the scheme's own shadow role, which is what the phone casts.
  shadows: [
    'none',
    '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    '0 2px 8px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.04)',
    '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    ...Array(21).fill('0 8px 28px rgba(0, 0, 0, 0.10)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: scheme.surface, color: scheme.onSurface },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 8,
          paddingInline: 16,
          boxShadow: 'none',
          transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease',
          '&:active': { transform: 'scale(0.985)' },
        },
        contained: {
          '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.10)' },
        },
        outlined: {
          borderColor: scheme.outlineVariant,
          backgroundColor: scheme.surfaceContainerLowest,
        },
      },
    },
    // Selected page sits on a primary.main fill, so its text must use the scheme's own
    // onPrimary — not a hardcoded white, and no longer a black/white guess either.
    MuiPaginationItem: {
      styleOverrides: {
        root: { '&.Mui-selected': { color: primaryPalette.contrastText } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 42,
          borderRadius: 8,
          backgroundColor: scheme.surfaceContainerLowest,
          '& fieldset': { borderColor: scheme.outlineVariant },
          '&:hover fieldset': { borderColor: scheme.outline },
          '&.Mui-focused fieldset': { borderWidth: '1px' },
        },
        input: { padding: '10px 12px', fontSize: '0.875rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.875rem' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { height: 26, borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, border: `1px solid ${scheme.outlineVariant}` } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { border: `1px solid ${scheme.outlineVariant}`, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 6, fontSize: '0.75rem' } },
    },
    // Tables deliberately opt OUT of the Material 3 scheme and use the neutral --app-* set
    // instead. M3 tints every neutral with the seed hue (a maroon tenant gives surface
    // #fff8f6, outlineVariant #ddc0bb), which reads as a coloured wash across a dense grid.
    // Brand colour still reaches tables through actions, links and selection — only the
    // chrome stays neutral, matching how tables render on dev.
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: 'var(--app-border-light)' },
        head: {
          color: 'var(--app-text-secondary)',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
        body: { color: 'var(--app-text)', fontSize: '0.8125rem' },
      },
    },
  },
}) as any

export default theme
